import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Paper from "@mui/material/Paper";
import reimbursementCategoryApiService, {
  ReimbursementCategory,
} from "../../services/reimbursement-category-api";

interface ReimbursementCategoriesCardProps {
  onSave?: () => void;
  hideHeader?: boolean;
  shouldLoad?: boolean;
}

export interface ReimbursementCategoriesCardRef {
  openAddDialog: () => void;
}

const ReimbursementCategoriesCard = forwardRef<
  ReimbursementCategoriesCardRef,
  ReimbursementCategoriesCardProps
>(({ onSave, hideHeader = false, shouldLoad = false }, ref) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [categories, setCategories] = useState<ReimbursementCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ReimbursementCategory>({
    englishName: "",
    fullwidthName: "",
  });
  const [draggedItem, setDraggedItem] = useState<number | null>(null);

  useEffect(() => {
    if (shouldLoad) {
      loadCategories();
    }
  }, [shouldLoad]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    openAddDialog: () => handleOpenDialog(),
  }));

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await reimbursementCategoryApiService.getAllCategories();
      setCategories(data);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("admin.errorDebitAccountLoad"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (category?: ReimbursementCategory) => {
    if (category) {
      setEditingId(category.id || null);
      setFormData(category);
    } else {
      setEditingId(null);
      setFormData({ englishName: "", fullwidthName: "" });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({ englishName: "", fullwidthName: "" });
  };

  const handleSaveCategory = async () => {
    if (!formData.englishName.trim() || !formData.fullwidthName.trim()) {
      setError(t("admin.debitAccountRequired"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (editingId) {
        await reimbursementCategoryApiService.updateCategory(
          editingId,
          formData,
        );
        setSuccess(t("admin.debitAccountUpdated"));
      } else {
        await reimbursementCategoryApiService.createCategory(formData);
        setSuccess(t("admin.debitAccountCreated"));
      }

      handleCloseDialog();
      await loadCategories();
      onSave?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("admin.errorDebitAccount"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (!window.confirm(t("admin.debitAccountDeleteConfirm"))) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await reimbursementCategoryApiService.deleteCategory(id);
      setSuccess(t("admin.debitAccountDeleted"));
      await loadCategories();
      onSave?.();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("admin.errorDebitAccountDelete"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (id: number) => {
    setDraggedItem(id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (targetId: number) => {
    if (draggedItem === null || draggedItem === targetId) return;

    const draggedIndex = categories.findIndex((c) => c.id === draggedItem);
    const targetIndex = categories.findIndex((c) => c.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newCategories = [...categories];
    const draggedItem_obj = newCategories[draggedIndex];

    // Remove the dragged item from its current position
    newCategories.splice(draggedIndex, 1);

    // Insert it at the target position
    newCategories.splice(targetIndex, 0, draggedItem_obj);

    setCategories(newCategories);
    setDraggedItem(null);

    try {
      const ids = newCategories.map((c) => c.id!);
      await reimbursementCategoryApiService.reorderCategories(ids);
      setSuccess(t("admin.debitAccountReordered"));
    } catch (err) {
      setError(t("admin.debitAccountReorderError"));
      await loadCategories();
    }
  };

  return (
    <Card sx={{ boxShadow: hideHeader ? "none" : "0 2px 8px rgba(0,0,0,0.1)" }}>
      <CardContent sx={{ p: hideHeader ? 0 : undefined }}>
        {!hideHeader && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t("admin.debitAccounts") || "Debit Accounts"}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                {t("admin.debitAccountsNote")}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={loading}
              size="medium"
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {t("common.add")}
            </Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && categories.length > 0 && (
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 1, border: "1px solid #e0e0e0" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="40px" sx={{ fontWeight: 700 }}></TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("common.category")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("common.ledgerValue")}
                  </TableCell>
                  <TableCell
                    width="100px"
                    align="center"
                    sx={{ fontWeight: 700 }}
                  >
                    {t("common.actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category) => (
                  <TableRow
                    key={category.id}
                    draggable
                    onDragStart={() => handleDragStart(category.id!)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(category.id!)}
                    sx={{
                      cursor: "move",
                      backgroundColor:
                        draggedItem === category.id
                          ? theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.12)"
                            : "#f0f0f0"
                          : "inherit",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "#fafafa",
                      },
                      borderBottom:
                        theme.palette.mode === "dark"
                          ? "1px solid rgba(255, 255, 255, 0.12)"
                          : "1px solid #e0e0e0",
                    }}
                  >
                    <TableCell align="center" sx={{ color: "#999" }}>
                      <DragIndicatorIcon fontSize="small" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {category.englishName}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {category.fullwidthName}
                    </TableCell>
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <Tooltip title={t("config.tooltipEdit")}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(category)}
                            disabled={loading}
                            sx={{ color: "primary.main" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("config.tooltipDelete")}>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteCategory(category.id!)}
                            disabled={loading}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && categories.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography color="textSecondary" sx={{ mb: 2 }}>
              {t("common.noData")}
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              size="small"
            >
              Create First Account
            </Button>
          </Box>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? t("common.editAccount") : t("common.addAccount")}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label={t("common.category")}
              value={formData.englishName}
              onChange={(e) =>
                setFormData({ ...formData, englishName: e.target.value })
              }
              placeholder="e.g., Consumable / Office Supplies"
              variant="outlined"
            />
            <TextField
              fullWidth
              label={t("common.ledgerValue")}
              value={formData.fullwidthName}
              onChange={(e) =>
                setFormData({ ...formData, fullwidthName: e.target.value })
              }
              placeholder="e.g., 消耗品費"
              variant="outlined"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>{t("common.cancel")}</Button>
          <Button
            onClick={handleSaveCategory}
            variant="contained"
            disabled={loading}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
});

ReimbursementCategoriesCard.displayName = "ReimbursementCategoriesCard";

export default React.memo(ReimbursementCategoriesCard);
